import { ComponentType, ReactNode, Suspense, lazy } from 'react'
import { Navigate } from 'react-router-dom'
import { RouteObject } from 'react-router'

import SidebarLayout from '../pages/admins/layouts/SidebarLayout'
import BaseLayout from '../pages/admins/layouts/BaseLayout'
import AuthTheme from '../pages/auth/authBrand'

import SuspenseLoader from '../components/SuspenseLoader'
import { elearningResultsRoutes } from './elearningRoutes'

const Loader =
  <P extends object>(Component: ComponentType<P>) =>
  (props: P): ReactNode =>
    (
      <Suspense fallback={<SuspenseLoader />}>
        <Component {...props} />
      </Suspense>
    )

// Pages
const Overview = Loader(lazy(() => import('../pages/admins/content/overview')))

// Applications
// FB-5: web practitioners (behandelaar) have no AI chat — the chat
// surface is replaced by a Helpdesk contact page (client KB chatbot
// lives on mobile per US-3.5). The old chat-ai path redirects to it.
const Helpdesk = Loader(
  lazy(() => import('../pages/admins/content/applications/Helpdesk'))
)
const UserProfile = Loader(
  lazy(() => import('../pages/admins/content/applications/Users/profile'))
)
const UserSettings = Loader(
  lazy(() => import('../pages/admins/content/applications/Users/settings'))
)
const Clients = Loader(
  lazy(() => import('../pages/admins/content/applications/Clients'))
)
const ClientDetail = Loader(
  lazy(() => import('../pages/admins/content/applications/Clients/detail'))
)

// management
const ManagementUploadData = Loader(
  lazy(() => import('../pages/admins/content/management/UploadData'))
)
const ManagementBotTrainer = Loader(
  lazy(() => import('../pages/admins/content/management/BotTrainer'))
)
const ManagementSetting = Loader(
  lazy(() => import('../pages/admins/content/management/Setting'))
)
const ManagementInvite = Loader(
  lazy(() => import('../pages/admins/content/management/Invite'))
)

// Status
// const Status404 = Loader(
//   lazy(() => import('../pages/admins/content/pages/Status/Status404'))
// )

// Auth
const AuthVerifyAccount = Loader(
  lazy(() => import('../pages/auth/verifyAccount'))
)
const AuthOtpCode = Loader(lazy(() => import('../pages/auth/otpCode')))
const AuthForgotPassword = Loader(
  lazy(() => import('../pages/auth/forgotPassword'))
)
const ResetPassword = Loader(lazy(() => import('../pages/auth/resetPassword')))
const Invitation = Loader(lazy(() => import('../pages/auth/invitation')))

const routesSupervisor: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="/dashboards" replace />,
      },
      {
        path: '/auth',
        element: <AuthTheme />,
        children: [
          {
            path: '',
            element: <Navigate to="login" replace />,
          },
          {
            path: 'forgot-password',
            element: <AuthForgotPassword />,
          },
          {
            path: 'verify-email/:token',
            element: <AuthVerifyAccount />,
          },
          {
            path: 'otp-code',
            element: <AuthOtpCode />,
          },
          {
            path: 'reset-password',
            element: <ResetPassword />,
          },
          {
            path: 'invitation/:token',
            element: <Invitation />,
          },
        ],
      },
      {
        path: '/dashboards',
        element: <SidebarLayout />,
        children: [
          {
            // Behandelaar lands on their client oversight list — their
            // actual workspace. (Was chat-ai; chat retired for
            // practitioners per FB-5.)
            path: '',
            element: <Navigate to="clienten" replace />,
          },
          {
            path: 'helpdesk',
            element: <Helpdesk />,
          },
          {
            // Redirect stale chat-ai bookmarks/links to the helpdesk.
            path: 'chat-ai',
            element: <Navigate to="/dashboards/helpdesk" replace />,
          },
          // Behandelaar client oversight (Module 9). Reads via
          // `/v1/supervisor/clients*` — IDOR-gated by `is_assigned` on the BE.
          {
            path: 'clienten',
            element: <Clients />,
          },
          {
            path: 'clienten/:id',
            element: <ClientDetail />,
          },
          // Supervisor scope = monitor clients + chat. Personal wellness
          // surface (kalender / feeling-chart / psychologie) is gated to
          // ClientAuthUser on the BE — never expose to supervisor here.
          {
            path: 'profile',
            children: [
              {
                path: '',
                element: <Navigate to="details" replace />,
              },
              {
                path: 'details',
                element: <UserProfile />,
              },
              {
                path: 'settings',
                element: <UserSettings />,
              },
            ],
          },
        ],
      },
      {
        path: 'management',
        element: <SidebarLayout />,
        children: [
          {
            path: '',
            element: <Navigate to="upload-data" replace />,
          },
          {
            path: 'bot-trainer',
            element: <ManagementBotTrainer />,
          },
          {
            path: 'upload-data',
            element: <ManagementUploadData />,
          },
          // Behandelaar can invite/create clients (Module 9 Slice 3).
          // The new client is auto-assigned to the inviting behandelaar
          // on join (see bloom-auth::invite::handle_join).
          {
            path: 'invite',
            element: <ManagementInvite />,
          },
          {
            path: 'setting',
            element: <ManagementSetting />,
          },
          // E-learning results of the behandelaar's own clients (read-only).
          ...elearningResultsRoutes,
        ],
      },
      {
        path: 'overview',
        element: <Overview />,
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
]

export default routesSupervisor
